import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { webappAccess, parkingZones } from '../services/api';
import concessionsApi from '../services/concessions';
import './WebappAccess.css';

const ZONE_RESTRICTABLE_MODULE = 'inventaire';

function keyOf(conc_id, module) {
  return `${conc_id}:${module}`;
}

function cloneZonesMap(src) {
  return new Map(Array.from(src.entries()).map(([k, v]) => [k, new Set(v)]));
}

function WebappAccessMatrix({ user, editMode, setHeaderAction }) {
  const [modules, setModules] = useState([]);
  const [allConcessions, setAllConcessions] = useState([]);
  const [allZones, setAllZones] = useState([]); // [{ id, concession_id, nom, ordre }]
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState(new Map());
  const [initialMatrix, setInitialMatrix] = useState(new Map());
  // Map<concession_id, Set<zone_id>> : zones explicitement cochées (whitelist).
  // Aucune entrée OU Set vide pour une concession = accès à toutes ses zones (défaut).
  const [allowedZonesByConc, setAllowedZonesByConc] = useState(new Map());
  const [initialAllowedZonesByConc, setInitialAllowedZonesByConc] = useState(new Map());
  const [zonePanelConcessionId, setZonePanelConcessionId] = useState(null);

  const canEdit = editMode === true;
  const isSuperAdmin = user?.role === 'super-admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modsRes, accessRes, concRes, zonesRes] = await Promise.all([
        webappAccess.getModules(),
        webappAccess.getAccess(),
        concessionsApi.getAll(),
        parkingZones.list(),
      ]);
      setModules(modsRes.data.modules || []);
      setUsers(accessRes.data.users || []);
      setAllConcessions(concRes.data.concessions || concRes.data || []);
      setAllZones(zonesRes.data.zones || []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  // Index zones par concession (pour l'affichage du panneau et le compteur "X/Y")
  const zonesByConcession = useMemo(() => {
    const m = new Map();
    for (const z of allZones) {
      if (!m.has(z.concession_id)) m.set(z.concession_id, []);
      m.get(z.concession_id).push(z);
    }
    return m;
  }, [allZones]);

  // Index inverse : zone_id → concession_id (pour reconstruire allowedZonesByConc depuis allowed_zones)
  const zoneIdToConcessionId = useMemo(() => {
    const m = new Map();
    for (const z of allZones) m.set(z.id, z.concession_id);
    return m;
  }, [allZones]);

  useEffect(() => {
    if (!selectedUser) {
      setMatrix(new Map());
      setInitialMatrix(new Map());
      setAllowedZonesByConc(new Map());
      setInitialAllowedZonesByConc(new Map());
      return;
    }
    const m = new Map();
    for (const entry of selectedUser.access || []) {
      m.set(keyOf(entry.concession_id, entry.module), true);
    }
    setMatrix(new Map(m));
    setInitialMatrix(new Map(m));

    const az = new Map();
    for (const zid of selectedUser.allowed_zones || []) {
      const cid = zoneIdToConcessionId.get(zid);
      if (cid == null) continue; // zone supprimée entre-temps
      if (!az.has(cid)) az.set(cid, new Set());
      az.get(cid).add(zid);
    }
    setAllowedZonesByConc(az);
    setInitialAllowedZonesByConc(cloneZonesMap(az));
  }, [selectedUser, zoneIdToConcessionId]);

  const dirty = useMemo(() => {
    if (matrix.size !== initialMatrix.size) return true;
    for (const k of matrix.keys()) if (!initialMatrix.has(k)) return true;
    // Comparaison zones : union des concessions présentes dans l'un ou l'autre
    const allConcIds = new Set([
      ...allowedZonesByConc.keys(),
      ...initialAllowedZonesByConc.keys(),
    ]);
    for (const cid of allConcIds) {
      const cur = allowedZonesByConc.get(cid) || new Set();
      const init = initialAllowedZonesByConc.get(cid) || new Set();
      if (cur.size !== init.size) return true;
      for (const z of cur) if (!init.has(z)) return true;
    }
    return false;
  }, [matrix, initialMatrix, allowedZonesByConc, initialAllowedZonesByConc]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.nom || '').toLowerCase().includes(q) ||
      (u.prenom || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  // Nettoie les zones d'une concession quand l'accès inventaire est retiré
  const dropZonesForConcession = (conc_id) => {
    setAllowedZonesByConc(prev => {
      if (!prev.has(conc_id)) return prev;
      const next = new Map(prev);
      next.delete(conc_id);
      return next;
    });
  };

  const toggleCell = (conc_id, module) => {
    if (!canEdit) return;
    const key = keyOf(conc_id, module);
    setMatrix(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
        if (module === ZONE_RESTRICTABLE_MODULE) dropZonesForConcession(conc_id);
      } else {
        next.set(key, true);
      }
      return next;
    });
  };

  const toggleRow = (conc_id) => {
    if (!canEdit) return;
    setMatrix(prev => {
      const next = new Map(prev);
      const allChecked = modules.every(m => next.has(keyOf(conc_id, m.id)));
      for (const m of modules) {
        const k = keyOf(conc_id, m.id);
        if (allChecked) next.delete(k);
        else next.set(k, true);
      }
      if (allChecked) dropZonesForConcession(conc_id);
      return next;
    });
  };

  const toggleCol = (module) => {
    if (!canEdit) return;
    setMatrix(prev => {
      const next = new Map(prev);
      const allChecked = allConcessions.every(c => next.has(keyOf(c.id, module)));
      for (const c of allConcessions) {
        const k = keyOf(c.id, module);
        if (allChecked) next.delete(k);
        else next.set(k, true);
      }
      if (allChecked && module === ZONE_RESTRICTABLE_MODULE) {
        // On retire toutes les restrictions de zones (la colonne inventaire vient d'être décochée partout)
        setAllowedZonesByConc(new Map());
      }
      return next;
    });
  };

  const toggleZone = (conc_id, zone_id) => {
    if (!canEdit) return;
    setAllowedZonesByConc(prev => {
      const next = new Map(prev);
      const set = new Set(next.get(conc_id) || []);
      if (set.has(zone_id)) set.delete(zone_id);
      else set.add(zone_id);
      if (set.size === 0) next.delete(conc_id);
      else next.set(conc_id, set);
      return next;
    });
  };

  const checkAllZones = (conc_id) => {
    if (!canEdit) return;
    const all = zonesByConcession.get(conc_id) || [];
    if (all.length === 0) return;
    setAllowedZonesByConc(prev => {
      const next = new Map(prev);
      next.set(conc_id, new Set(all.map(z => z.id)));
      return next;
    });
  };

  const clearAllZones = (conc_id) => {
    if (!canEdit) return;
    setAllowedZonesByConc(prev => {
      if (!prev.has(conc_id)) return prev;
      const next = new Map(prev);
      next.delete(conc_id);
      return next;
    });
  };

  const save = useCallback(async () => {
    if (!selectedUser || !dirty) return;
    setSaving(true);
    try {
      const access = Array.from(matrix.keys()).map(k => {
        const [cid, mod] = k.split(':');
        return { concession_id: parseInt(cid, 10), module: mod };
      });
      // Aplatissement des zones autorisées (uniquement pour concessions où inventaire est coché)
      const allowed_zones = [];
      for (const [cid, set] of allowedZonesByConc.entries()) {
        if (!matrix.has(keyOf(cid, ZONE_RESTRICTABLE_MODULE))) continue; // safety
        for (const zid of set) allowed_zones.push(zid);
      }
      await webappAccess.saveAccess(selectedUser.id, access, allowed_zones);
      await load();
      setSelectedUserId(selectedUser.id);
    } catch (e) {
      console.error(e);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }, [selectedUser, dirty, matrix, allowedZonesByConc, load]);

  const clearAll = async () => {
    if (!selectedUser || !isSuperAdmin) return;
    if (!window.confirm(`Retirer tous les accès webapp de ${selectedUser.prenom} ${selectedUser.nom} ?`)) return;
    try {
      await webappAccess.clearAccess(selectedUser.id);
      await load();
      setSelectedUserId(selectedUser.id);
    } catch (e) {
      console.error(e);
      alert('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    if (!setHeaderAction) return;
    if (selectedUser && dirty && canEdit) {
      setHeaderAction({
        primary: { label: saving ? 'Enregistrement…' : 'Enregistrer', onClick: save },
      });
    } else {
      setHeaderAction(null);
    }
    return () => setHeaderAction && setHeaderAction(null);
  }, [setHeaderAction, selectedUser, dirty, canEdit, saving, save]);

  if (loading) return <p>Chargement…</p>;
  if (error) return <p style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>{error}</p>;

  return (
    <div className="webapp-access">
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher un utilisateur…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {filteredUsers.length} utilisateur(s) • {modules.length} module(s) • {allConcessions.length} concession(s)
        </span>
      </div>

      <div className="webapp-access-layout">
        <div className="webapp-access-userlist">
          <div className="webapp-access-userlist-header">Utilisateurs</div>
          <ul>
            {filteredUsers.map(u => {
              const count = (u.access || []).length;
              return (
                <li
                  key={u.id}
                  className={u.id === selectedUserId ? 'selected' : ''}
                  onClick={() => {
                    if (dirty && !window.confirm('Changements non enregistrés. Les abandonner ?')) return;
                    setSelectedUserId(u.id);
                  }}
                >
                  <div className="webapp-access-username">
                    <strong>{u.prenom} {u.nom}</strong>
                    <span className="webapp-access-badge">{count}</span>
                  </div>
                  <div className="webapp-access-userinfo">
                    <span>{u.username || u.email}</span>
                    <span className="webapp-access-role">{u.role}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="webapp-access-matrix-panel">
          {!selectedUser ? (
            <div className="webapp-access-empty">
              <p>Sélectionnez un utilisateur pour gérer ses accès webapp.</p>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Les accès sont organisés en matrice <strong>module × concession</strong>.
                Un utilisateur peut voir un module donné seulement sur les concessions cochées.
              </p>
            </div>
          ) : (
            <>
              <div className="webapp-access-matrix-header">
                <div>
                  <h3>{selectedUser.prenom} {selectedUser.nom}</h3>
                  <span className="webapp-access-subtitle">
                    {selectedUser.username || selectedUser.email} • {selectedUser.role}
                    {!canEdit && <> — <em>Mode édition désactivé (lecture seule)</em></>}
                  </span>
                </div>
                {canEdit && isSuperAdmin && (
                  <button className="btn-icon delete" title="Retirer tous les accès webapp" onClick={clearAll}>
                    🗑️
                  </button>
                )}
              </div>

              <div className="webapp-access-matrix-wrapper">
                <table className="webapp-access-matrix">
                  <thead>
                    <tr>
                      <th className="sticky-col">Concession</th>
                      {modules.map(m => (
                        <th
                          key={m.id}
                          className={canEdit ? 'clickable' : ''}
                          onClick={() => toggleCol(m.id)}
                          title={canEdit ? 'Cliquer pour inverser la colonne' : m.label}
                        >
                          <div className="webapp-access-col-label">
                            <span className="webapp-access-col-icon">{m.icon}</span>
                            <span>{m.label}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allConcessions.map(c => (
                      <tr key={c.id}>
                        <td
                          className={`sticky-col ${canEdit ? 'clickable' : ''}`}
                          onClick={() => toggleRow(c.id)}
                          title={canEdit ? 'Cliquer pour inverser la ligne' : c.nom}
                        >
                          {c.nom}
                        </td>
                        {modules.map(m => {
                          const checked = matrix.has(keyOf(c.id, m.id));
                          const isZoneRestrictable = m.id === ZONE_RESTRICTABLE_MODULE;
                          const restrictedSet = allowedZonesByConc.get(c.id);
                          const restricted = isZoneRestrictable && restrictedSet && restrictedSet.size > 0;
                          const totalZones = isZoneRestrictable ? (zonesByConcession.get(c.id) || []).length : 0;
                          return (
                            <td key={m.id} className="webapp-access-cell">
                              <input
                                type="checkbox"
                                disabled={!canEdit}
                                checked={checked}
                                onChange={() => toggleCell(c.id, m.id)}
                              />
                              {isZoneRestrictable && checked && (
                                <button
                                  type="button"
                                  className={`webapp-access-zone-pill ${restricted ? 'restricted' : ''}`}
                                  title={restricted
                                    ? `Restreint à ${restrictedSet.size}/${totalZones} zone(s) — cliquer pour modifier`
                                    : (totalZones === 0
                                        ? 'Aucune zone définie pour cette concession'
                                        : 'Accès à toutes les zones — cliquer pour restreindre')}
                                  onClick={(e) => { e.stopPropagation(); setZonePanelConcessionId(c.id); }}
                                  disabled={totalZones === 0}
                                >
                                  {restricted
                                    ? `${restrictedSet.size}/${totalZones}`
                                    : (totalZones === 0 ? '—' : 'Toutes')}
                                </button>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {allConcessions.length === 0 && (
                      <tr>
                        <td colSpan={modules.length + 1} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                          Aucune concession définie.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {dirty && canEdit && (
                <div className="webapp-access-dirty-hint">
                  ⚠️ Modifications non enregistrées. Utiliser le bouton « Enregistrer » en haut à droite.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {zonePanelConcessionId != null && (() => {
        const conc = allConcessions.find(c => c.id === zonePanelConcessionId);
        const zones = zonesByConcession.get(zonePanelConcessionId) || [];
        const restrictedSet = allowedZonesByConc.get(zonePanelConcessionId) || new Set();
        const close = () => setZonePanelConcessionId(null);
        return (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📍 Zones autorisées — {conc ? conc.nom : ''}</h2>
                <button className="modal-close" onClick={close}>✕</button>
              </div>
              <div className="modal-body">
                <p className="webapp-access-zone-hint">
                  Aucune case cochée = accès à <strong>toutes les zones</strong> (défaut).
                  Cocher une ou plusieurs zones = accès <strong>uniquement</strong> à ces zones.
                </p>
                {zones.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
                    Aucune zone définie pour cette concession.
                  </p>
                ) : (
                  <div className="webapp-access-zone-list">
                    {zones.map(z => {
                      const checked = restrictedSet.has(z.id);
                      return (
                        <label key={z.id} className="webapp-access-zone-row">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => toggleZone(zonePanelConcessionId, z.id)}
                          />
                          <span>{z.nom}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {canEdit && zones.length > 0 && (
                  <>
                    <button className="btn-secondary" onClick={() => clearAllZones(zonePanelConcessionId)}>
                      Tout décocher (= toutes)
                    </button>
                    <button className="btn-secondary" onClick={() => checkAllZones(zonePanelConcessionId)}>
                      Tout cocher
                    </button>
                  </>
                )}
                <button className="btn-primary" onClick={close}>OK</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default WebappAccessMatrix;
